import type { components as internalComponents } from '@/generated/internal-api/openapi-types';
import type { components as messagingComponents } from '@/generated/messaging-api/openapi-types';

export type AgentType = internalComponents['schemas']['AgentType.schema'];
export type AgentSessionApiId = internalComponents['schemas']['AgentSessionApiId.schema'];
export type AgentSessionStatus = internalComponents['schemas']['AgentSessionStatus.schema'];
export type AgentDefinitionV1 = internalComponents['schemas']['AgentDefinitionV1.schema'];
export type AgentSessionSummaryV1 = internalComponents['schemas']['AgentSessionSummaryV1.schema'];
export type AgentSessionV1 = internalComponents['schemas']['AgentSessionV1.schema'];

export type AgentTreeV1 = internalComponents['schemas']['AgentTreeV1.schema'];
export type AgentTreeNodeV1 = internalComponents['schemas']['AgentTreeNodeV1.schema'];
export type AgentTreeSessionV1 = internalComponents['schemas']['AgentTreeSessionV1.schema'];
export type AgentInputV1 = internalComponents['schemas']['AgentInputV1.schema'];
export type AgentTreeNodeApiId = internalComponents['schemas']['AgentTreeNodeApiId.schema'];
export type AgentTreeNodeType = internalComponents['schemas']['AgentTreeNodeType.schema'];
export type AgentExecutionApiId = internalComponents['schemas']['AgentExecutionApiId.schema'];

/**
 * One entry of a session's durable stream-event history (`GET .../events`): the audit-row id (a
 * stable React key), the recomposed Redis stream id (`<ms>-<seq>`) when the event reached the live
 * stream else null, and the deserialised {@link AgentStreamEventV1} payload.
 */
export type AgentExecutionEventV1 = internalComponents['schemas']['AgentExecutionEventV1.schema'];

type GeneratedAgentStreamEventV1 = messagingComponents['schemas']['AgentStreamEventV1.schema'];

export type AgentStreamEventType = GeneratedAgentStreamEventV1['type'];

/**
 * The values a run can terminate on. Each is BOTH a stream event type and a session status (the
 * terminal event type IS the resulting status), so the values satisfy the intersection — usable to
 * subscribe to the stream event AND to set the session status without a cast. Single source for the
 * two terminal Sets below.
 */
export const TerminalEventStatus = {
  Completed: 'completed',
  Failed: 'failed',
  Cancelled: 'cancelled',
} as const satisfies Record<string, AgentStreamEventType & AgentSessionStatus>;
export type TerminalEventStatus = (typeof TerminalEventStatus)[keyof typeof TerminalEventStatus];

/** Stream event types that close the stream (no further events expected). */
export const TERMINAL_STREAM_EVENTS: ReadonlySet<AgentStreamEventType> = new Set(Object.values(TerminalEventStatus));

/** Session statuses that mean a run has finished (no further events expected). */
export const TERMINAL_SESSION_STATUSES: ReadonlySet<AgentSessionStatus> = new Set(Object.values(TerminalEventStatus));

type WithId<T> = T extends unknown ? T & { id?: string } : never;

/**
 * Stream event delivered over SSE. The optional `id` is injected by useAgentStream
 * from the SSE lastEventId so the reducer can deduplicate replays on reconnect.
 */
export type AgentStreamEventV1 = WithId<GeneratedAgentStreamEventV1>;

export type AgentSessionPhase = 'idle' | 'creating' | 'streaming' | 'completed' | 'failed' | 'cancelled';

/**
 * SEND Validator result model. These types mirror the server records under
 * `com.overwatchresearch.ai.send.model` (snake_case on the wire) and are used to
 * read `execution.result`/`session.result` JSONB — they are not generated from
 * OpenAPI because the findings blob is stored as opaque JSONB, not a typed schema.
 */

/** Severity of a SEND validation finding. */
export type Severity = 'error' | 'warning' | 'info';

/** The kind of check that produced a SEND validation finding. */
export type FindingCategory =
  | 'required_column'
  | 'column_name'
  | 'column_order'
  | 'data_type'
  | 'required_field'
  | 'iso_8601_date'
  | 'controlled_terminology'
  | 'cross_field'
  | 'statistical_anomaly';

/** A single SEND validation issue. */
export interface Finding {
  severity: Severity;
  category: FindingCategory;
  column_name?: string | null;
  row_number?: number | null;
  current_value?: string | null;
  expected_value?: string | null;
  message: string;
}

/** Aggregate counts across all findings for a validation run. */
export interface FindingsSummary {
  error_count: number;
  warning_count: number;
  info_count: number;
  submission_blocking_count: number;
}

/** Records that findings for a category were capped ("showing first N of M"). */
export interface Truncation {
  category: string;
  omitted_count: number;
}

/**
 * The full structured outcome of a SEND validation run, persisted to
 * `agent_executions.result`. The UI paginates `findings` client-side.
 */
export interface SendValidationResult {
  status: string;
  domain_code: string;
  row_count: number;
  summary: FindingsSummary;
  findings: Array<Finding>;
  truncation?: Array<Truncation> | null;
}

/** A ranked candidate domain produced by header-fingerprint scoring during detection. */
export interface SendDomainCandidate {
  domain_code: string;
  domain_name?: string | null;
  confidence: number;
}

/**
 * The compact detection outcome returned by execution 1. When `status` is
 * `requires_input` the UI renders the detected domain plus a closed-list override
 * so the user can confirm/change it before validation runs.
 */
export interface SendDetectionResult {
  status: 'requires_input' | 'validated';
  detected_domain: string;
  candidates: Array<SendDomainCandidate>;
}

export interface StreamingText {
  executionApiId: AgentExecutionApiId;
  accumulatedText: string;
  isStreaming: boolean;
}
